// ================================
// AUTH SESSION HELPERS
// The 30-day session lifetime is owned by the backend JWT, which is signed
// with `expiresIn: "30d"`. The token lives in localStorage, so it already
// survives page refreshes, browser restarts and computer reboots.
//
// These helpers let the client enforce that SAME expiry: the token's `exp`
// claim is the single source of truth. A token that is missing, malformed
// or past its `exp` is treated as logged-out and its data is cleared
// automatically, so an expired session can never keep a user "in".
// ================================

// Every localStorage key that holds authentication state.
const AUTH_KEYS = ["token", "username", "userId", "isAdmin", "redirectAfterLogin"];

// Decode a JWT payload (base64url) without any external dependency.
// Returns null for anything that isn't a well-formed JWT.
function decodeToken(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    return JSON.parse(json);
  } catch {
    return null;
  }
}

// Remove every piece of authentication data from storage.
export function clearSession() {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
}

// True only when a valid, unexpired token is present.
// Side-effect: a present-but-invalid/expired token is cleared so the app
// never holds on to a dead session.
export function isAuthenticated() {
  const token = localStorage.getItem("token");
  if (!token) return false;

  const payload = decodeToken(token);

  // Malformed token, or no expiry claim -> treat as invalid.
  if (!payload || !payload.exp) {
    clearSession();
    return false;
  }

  // `exp` is in seconds since epoch; clear and reject once it has passed.
  if (payload.exp * 1000 <= Date.now()) {
    clearSession();
    return false;
  }

  return true;
}

// True when the current valid session belongs to an admin.
export function isAdmin() {
  return isAuthenticated() && localStorage.getItem("isAdmin") === "true";
}

// ================================
// SERVER CONFIRMATION
// isAuthenticated() only DECODES the token — it cannot check the signature, so
// a token typed into DevTools, or one left over from an account that has since
// been blocked or still owes a verification step, would open a protected page
// by direct URL or deep link. confirmSession() asks the backend (/api/auth/me,
// which verifies the signature and returns the account's verification flags)
// once per token per page load, and says where the visitor belongs instead.
//
// Only a definitive "ok" is cached. A network failure or a sleeping backend
// answers "unknown", which the route guard lets through: the API refuses an
// unverified or forged token on its own (middleware/requireVerified.js), so a
// genuine user is never locked out by a cold start.
// ================================
const API_BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const CONFIRM_TIMEOUT_MS = 10000;
const confirmedTokens = new Set();

// Synchronous: has the CURRENT token already been confirmed on this page load?
export function isSessionConfirmed() {
  const token = localStorage.getItem("token");
  return !!token && confirmedTokens.has(token);
}

// Resolves to { status, email? } where status is one of
//   "ok"            the server accepts this session
//   "invalid"       forged / expired / deleted / blocked — log in again
//   "verify-email"  the account still owes its email OTP
//   "verify-face"   the account still owes its face check
//   "unknown"       the server could not be reached — let the API decide
// Never rejects. Any status other than ok/unknown clears the stored session.
export async function confirmSession() {
  const token = localStorage.getItem("token");
  if (!token) return { status: "invalid" };
  if (confirmedTokens.has(token)) return { status: "ok" };

  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), CONFIRM_TIMEOUT_MS) : null;

  let res;
  try {
    res = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl ? ctrl.signal : undefined,
    });
  } catch {
    return { status: "unknown" };
  } finally {
    if (timer) clearTimeout(timer);
  }

  if (res.status === 401 || res.status === 403 || res.status === 404) {
    clearSession();
    return { status: "invalid" };
  }
  if (!res.ok) return { status: "unknown" };

  let user = null;
  try {
    user = await res.json();
  } catch {
    return { status: "unknown" };
  }
  if (!user) return { status: "unknown" };

  // The same two rules the backend gates on (middleware/requireVerified.js).
  if (user.emailVerificationRequired === true && user.emailVerified !== true) {
    clearSession();
    return { status: "verify-email", email: user.email };
  }
  if (user.faceRequired === true && user.faceVerified !== true) {
    clearSession();
    return { status: "verify-face", email: user.email };
  }

  confirmedTokens.add(token);
  return { status: "ok" };
}
